import { useState } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  type Category,
} from "@/lib/api/vocabulary";
import { Trash2, Pencil, Sparkles } from "lucide-react";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { useGetProfile } from "@/lib/api/auth/hooks";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { LevelBadge } from "@/entities/vocabulary";
import {
  CategoryDialog,
  type CategoryDialogValues,
} from "@/widgets/vocabulary/category-dialog";
import { useUpdateCategoryWithConfirm } from "@/features/vocabulary";

function CategoriesPage() {
  const navigate = useNavigate();
  const { data: user, isLoading: isUserLoading } = useGetProfile();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();
  const officeId = user?.office?._id;
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(
    null
  );

  const { data: categories, isLoading } = useCategories(officeId || "");
  const { mutateAsync: createCategory, isPending: isCreating } =
    useCreateCategory();
  const { mutateAsync: deleteCategory, isPending: isDeleting } =
    useDeleteCategory();
  const { submit: submitEdit, isSubmitting: isEditing } =
    useUpdateCategoryWithConfirm(editingCategory ?? undefined);

  const handleCreateCategory = async (values: CategoryDialogValues) => {
    if (!officeId) return;

    await createCategory({
      name: values.name,
      words_per_page: values.words_per_page,
      office: officeId,
      level: values.level,
    });

    setIsCreateDialogOpen(false);
  };

  const handleEditCategory = async (values: CategoryDialogValues) => {
    if (!editingCategory) return;

    const saved = await submitEdit(editingCategory._id, {
      name: values.name,
      words_per_page: values.words_per_page,
      level: values.level,
    });

    if (saved) {
      setEditingCategory(null);
    }
  };

  const handleDeleteCategory = (id: string) => {
    setAlertDialogState({
      title: "카테고리 삭제",
      description: "카테고리를 삭제하시겠습니까?",
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        await deleteCategory(id);
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  const handleCategoryClick = (categoryId: string) => {
    navigate({ to: `/vocabulary/categories/${categoryId}/words` });
  };

  useLoadingIndicator(
    isUserLoading || isLoading || isDeleting || isCreating || isEditing
  );

  return (
    <PageContainer>
      <Header
        title="단어장 카테고리"
        description="카테고리를 관리합니다."
        right={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            카테고리 추가
          </Button>
        }
      />

      {!categories || categories.length === 0 ? (
        <div className="text-center text-gray-500 py-16 border rounded-lg">
          카테고리가 없습니다. 새 카테고리를 만들어주세요.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>카테고리</TableHead>
              <TableHead>난이도</TableHead>
              <TableHead>페이지당 단어 수</TableHead>
              <TableHead>마지막 페이지</TableHead>
              <TableHead>작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map((category) => {
              return (
                <TableRow
                  key={category._id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleCategoryClick(category._id)}
                >
                  <TableCell className="font-medium">{category.name}</TableCell>
                  <TableCell>
                    <LevelBadge level={category.level} />
                  </TableCell>
                  <TableCell>{category.words_per_page}</TableCell>
                  <TableCell>{category.last_page}</TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    className="space-x-2"
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        navigate({
                          to: `/vocabulary/categories/${category._id}/sentences`,
                        })
                      }
                      title="AI 예문 관리"
                    >
                      <Sparkles />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setEditingCategory(category)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleDeleteCategory(category._id)}
                    >
                      <Trash2 />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CategoryDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
        isSubmitting={isCreating}
        onSubmit={handleCreateCategory}
      />

      <CategoryDialog
        open={!!editingCategory}
        onOpenChange={(open) => {
          if (!open) setEditingCategory(null);
        }}
        mode="edit"
        initialValues={
          editingCategory
            ? {
                name: editingCategory.name,
                words_per_page: editingCategory.words_per_page,
                level: editingCategory.level,
              }
            : undefined
        }
        isSubmitting={isEditing}
        onSubmit={handleEditCategory}
      />
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/vocabulary/categories/")({
  component: CategoriesPage,
});
